import { useState } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import type { DatasetSummaryDto } from '@/api/types';

interface DeleteConfirmModalProps {
  dataset: DatasetSummaryDto;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

export function DeleteConfirmModal({ dataset, onClose, onConfirm }: DeleteConfirmModalProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (confirmError) {
      setError(confirmError instanceof Error ? confirmError.message : 'Could not delete the dataset.');
      setBusy(false);
    }
  };

  return (
    <Dialog title="Delete Dataset" onClose={onClose}>
      <div>
        <p style={{ margin: '0 0 var(--space-md)' }}>
          Are you sure you want to delete <strong>{dataset.displayName}</strong>? This removes its raw CSV files, candles, news, and related backtests.
        </p>
        {error && <p className="statusText statusTextDanger" role="alert">{error}</p>}
        <div style={{ display: 'flex', gap: 'var(--space-sm)', justifyContent: 'flex-end' }}>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" variant="danger" onClick={handleConfirm} disabled={busy}>
            {busy ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
